/**
 * What the "Delete transaction?" dialog says happens to each record linked to the transaction.
 *
 * THE SERVER SAYS WHAT HAPPENS, NEVER THIS FILE. `get_transaction_linked_records` gives every row an
 * `effect`, read from the foreign key behind it: `deleted` (ON DELETE CASCADE), `unlinked` (SET NULL,
 * the roadworthiness certificate), `unassigned` (the other side of a reversal, or a refund of the
 * deposit being deleted) and `blocks` (a NO ACTION link, on the transaction or one step away, which
 * makes the delete fail with 23503 and change nothing). This module only groups the rows under
 * headings and picks the sentences around them.
 *
 * A ROW WITHOUT AN EFFECT IS NOT A ROW THAT GETS DELETED. The RPC before fleet-backend 20261014010000
 * sends no `effect`, and a later one may send a value this file does not know. Either row goes under
 * "Also linked" and the headline hedges, because that list never shows the links one step away that
 * can still stop the delete. This is why the admin ships before the backend.
 */

/** One row of `get_transaction_linked_records`; `effect` is absent from an older backend's rows */
export interface LinkedRecord {
    type: string;
    label: string;
    context: string;
    effect?: string;
}

/** What deleting the transaction does to a linked record, `unknown` when the row does not say */
export type LinkedRecordEffect = 'blocks' | 'deleted' | 'unlinked' | 'unassigned' | 'unknown';

export interface LinkedRecordGroup {
    effect: LinkedRecordEffect;
    heading: string;
    records: LinkedRecord[];
}

/** The dialog's group order: what stops the delete first, what the dialog cannot vouch for last */
const EFFECT_ORDER: readonly LinkedRecordEffect[] = ['blocks', 'deleted', 'unlinked', 'unassigned', 'unknown'];

const HEADINGS: Record<LinkedRecordEffect, string> = {
    blocks: 'Stops the delete',
    deleted: 'Deleted with it',
    unlinked: 'Kept, but unlinked',
    unassigned: 'Returns to Unassigned',
    unknown: 'Also linked',
};

/** Once something stops the delete nothing happens to the other groups, so they say what would */
const BLOCKED_HEADINGS: Record<LinkedRecordEffect, string> = {
    blocks: 'Stops the delete',
    deleted: 'Would be deleted with it',
    unlinked: 'Would be kept, but unlinked',
    unassigned: 'Would return to Unassigned',
    unknown: 'Also linked',
};

const HEADLINE_DELETES = 'This will permanently delete this transaction.';
const HEADLINE_BLOCKED =
    'This transaction can’t be deleted yet. The records under “Stops the delete” still depend on it, so Delete will fail and nothing will change.';
const HEADLINE_UNSURE = 'This will permanently delete this transaction, unless a linked record stops it.';

/** The line under the headline when the list itself could not be read */
export const LINKED_RECORDS_UNREAD = 'Couldn’t check linked records.';

/** A delete refused by a foreign key (SQLSTATE 23503) rolls back whole, so nothing was changed */
const DELETE_BLOCKED_TOAST = 'Transaction not deleted: a linked record stops the delete. Nothing was changed.';

function effectOf(record: LinkedRecord): LinkedRecordEffect {
    switch (record.effect) {
        case 'blocks':
        case 'deleted':
        case 'unlinked':
        case 'unassigned':
            return record.effect;
        default:
            return 'unknown';
    }
}

/** The rows under their headings, in the dialog's order and the server's order within a group; empty groups are left out */
export function groupLinkedRecords(records: readonly LinkedRecord[]): LinkedRecordGroup[] {
    const headings = records.some(r => effectOf(r) === 'blocks') ? BLOCKED_HEADINGS : HEADINGS;
    return EFFECT_ORDER
        .map(effect => ({ effect, heading: headings[effect], records: records.filter(r => effectOf(r) === effect) }))
        .filter(group => group.records.length > 0);
}

/**
 * The dialog's first sentence. `records` is undefined while the list is unread (still loading, or it
 * failed), and then nothing is known about what might stop the delete.
 */
export function deleteHeadline(records: readonly LinkedRecord[] | undefined): string {
    if (!records) return HEADLINE_UNSURE;
    if (records.some(r => effectOf(r) === 'blocks')) return HEADLINE_BLOCKED;
    if (records.some(r => effectOf(r) === 'unknown')) return HEADLINE_UNSURE;
    return HEADLINE_DELETES;
}

/** The toast for a failed delete: the plain sentence when a linked record stops it, the error's own text otherwise */
export function deleteFailureMessage(error: { code?: string; message?: string }): string {
    if (error.code === '23503') return DELETE_BLOCKED_TOAST;
    return error.message || 'Failed to delete transaction';
}
